import React, { Suspense, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import HomePage from "@/pages/HomePage";
import SmallScreenNotice from "@/components/SmallScreenNotice";
import { partnerGet } from "@/services/partnerApi";

// Same-tab access-key change emitter. Native `storage` events only fire
// across tabs, so without this hook a same-tab `localStorage.setItem(
// 'cv_access_key', ...)` (or removeItem / clear) is invisible to other
// React subtrees mounted at the document root. The Copilot panel mounts
// in parallel with <App> and reads the key once at mount — if the
// analyzer hydrates the key asynchronously (very common for account-
// based / agency-customer sessions where /access-key/validate runs after
// mount) the panel was stuck in guest mode forever.
//
// Rather than touching ~80 setItem/removeItem call sites, we install a
// targeted Storage-prototype patch that fires `cv:accesskey-change` on
// every mutation of THIS specific key. The patch is idempotent and only
// matches `cv_access_key` so other keys are completely unaffected.
// `Storage.prototype.clear()` is also intercepted so a sign-out that
// nukes localStorage still notifies subscribers.
(function installCvAccessKeyEmitter() {
  if (typeof window === 'undefined') return;
  if (window.__cvAccessKeyPatched) return;
  window.__cvAccessKeyPatched = true;
  const fire = () => {
    try { window.dispatchEvent(new Event('cv:accesskey-change')); }
    catch { /* swallow */ }
  };
  try {
    const proto = window.Storage && window.Storage.prototype;
    if (!proto) return;
    const origSet = proto.setItem;
    const origRemove = proto.removeItem;
    const origClear = proto.clear;
    proto.setItem = function patchedSetItem(key, value) {
      const prev = this === window.localStorage ? this.getItem(key) : null;
      const ret = origSet.apply(this, arguments);
      if (this === window.localStorage && key === 'cv_access_key' && prev !== String(value)) fire();
      return ret;
    };
    proto.removeItem = function patchedRemoveItem(key) {
      const had = this === window.localStorage ? this.getItem(key) : null;
      const ret = origRemove.apply(this, arguments);
      if (this === window.localStorage && key === 'cv_access_key' && had != null) fire();
      return ret;
    };
    proto.clear = function patchedClear() {
      const had = this === window.localStorage ? this.getItem('cv_access_key') : null;
      const ret = origClear.apply(this, arguments);
      if (this === window.localStorage && had != null) fire();
      return ret;
    };
  } catch { /* swallow — falls back to the panel's 1.5s polling sync */ }
})();

// Browser-stable visitor id bootstrap. Generated once on first load,
// persisted in `localStorage['cv_visitor_id']` for the lifetime of the
// browser profile, and forwarded on every API call via the
// `x-cv-visitor-id` header. The worker reads this header to key the
// guest credit bucket (`credits:fp:<vid>`) so IPv6 privacy-extension
// rotation — which previously created a fresh `credits:<new_ip>`
// bucket with a full quota every few hours — no longer hands the
// visitor unlimited free credits (incident `cvh-0v0rpg`, 2026-05-10).
//
// Premium / agency / partner customers always key on
// `accessKey.record_id`, so this id is effectively a no-op for them —
// but we still mint and send it so the same code path works for the
// brief window between page load and access-key validation.
//
// The id is exposed on `window.__cvVisitorId` for ad-hoc consumers
// (axios interceptor, raw fetch + EventSource SSE calls) so each call
// site doesn't have to re-touch localStorage.
(function installCvVisitorIdBootstrap() {
  if (typeof window === 'undefined') return;
  try {
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
    let existing = '';
    try { existing = String(window.localStorage.getItem('cv_visitor_id') || '').trim().toLowerCase(); }
    catch { existing = ''; }
    let id = UUID_RE.test(existing) ? existing : '';
    if (!id) {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        id = window.crypto.randomUUID();
      } else {
        // Best-effort polyfill for very old browsers / file:// contexts.
        // RFC 4122 v4 with browser PRNG — collision probability is
        // astronomically small for this dataset (<10M visitors / day).
        id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
      }
      try { window.localStorage.setItem('cv_visitor_id', id); } catch { /* swallow */ }
    }
    window.__cvVisitorId = id;
  } catch {
    // localStorage blocked (private mode, embedded iframe) — fall back
    // to an in-memory id so the API surface still sees a stable value
    // for THIS pageload, and revert to IP-based bucketing only on the
    // next refresh.
    try {
      const fallback = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
      window.__cvVisitorId = fallback;
    } catch { window.__cvVisitorId = null; }
  }
})();

// Centralised SPA navigation emitter. Patches `history.pushState` and
// `history.replaceState` exactly once and emits `cv:locationchange` after
// every navigation (including hashchange / popstate). All consumers
// (telemetry hook, copilot panels, GA) listen for the single event so we
// avoid stacking pushState patches on top of each other.
(function installCvLocationEmitter() {
  if (typeof window === 'undefined') return;
  if (window.__cvLocationPatched) return;
  window.__cvLocationPatched = true;
  const fire = (kind) => {
    try {
      window.dispatchEvent(new CustomEvent('cv:locationchange', {
        detail: {
          path: window.location.pathname,
          search: window.location.search || '',
          hash: window.location.hash || '',
          kind: kind || 'pushstate',
        },
      }));
    } catch { /* swallow */ }
  };
  try {
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function patchedPushState(...args) {
      const ret = origPush.apply(this, args);
      fire('pushstate');
      return ret;
    };
    window.history.replaceState = function patchedReplaceState(...args) {
      const ret = origReplace.apply(this, args);
      fire('replacestate');
      return ret;
    };
    window.addEventListener('popstate', () => fire('popstate'));
    window.addEventListener('hashchange', () => fire('hashchange'));
  } catch { /* swallow */ }
})();

const App = React.lazy(() => import("@/App"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const PremiumSuccess = React.lazy(() => import("@/pages/PremiumSuccess"));
const PremiumCancel = React.lazy(() => import("@/pages/PremiumCancel"));
const RazorpayCallback = React.lazy(() => import("@/pages/RazorpayCallback"));
const TopupSuccess = React.lazy(() => import("@/pages/TopupSuccess"));
const LearningCenter = React.lazy(() => import("@/pages/LearningCenter"));
const LearningPageView = React.lazy(() => import("@/pages/LearningPageView"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const Partner = React.lazy(() => import("@/pages/Partner"));
const DomainDashboard = React.lazy(() => import("@/pages/DomainDashboard"));
const DashboardCopilotPanel = React.lazy(() => import("@/components/customer/DashboardCopilotPanel"));
const PublicCopilotPanel = React.lazy(() => import("@/components/PublicCopilotPanel"));

const AboutPage = React.lazy(() => import("@/pages/static/AboutPage"));
const AcceptableUsePage = React.lazy(() => import("@/pages/static/AcceptableUsePage"));
const BotPage = React.lazy(() => import("@/pages/static/BotPage"));
const ContactPage = React.lazy(() => import("@/pages/static/ContactPage"));
const CookiesPage = React.lazy(() => import("@/pages/static/CookiesPage"));
const DashboardGuidePage = React.lazy(() => import("@/pages/static/DashboardGuidePage"));
const DisclaimerPage = React.lazy(() => import("@/pages/static/DisclaimerPage"));
const FaqPage = React.lazy(() => import("@/pages/static/FaqPage"));
const KnowledgePage = React.lazy(() => import("@/pages/static/KnowledgePage"));
const PartnerAgreementPage = React.lazy(() => import("@/pages/static/PartnerAgreementPage"));
const PartnerDisclaimerPage = React.lazy(() => import("@/pages/static/PartnerDisclaimerPage"));
const PartnerFaqPage = React.lazy(() => import("@/pages/static/PartnerFaqPage"));
const PartnerKnowledgePage = React.lazy(() => import("@/pages/static/PartnerKnowledgePage"));
const PartnerProgramPage = React.lazy(() => import("@/pages/static/PartnerProgramPage"));
const PartnerTermsPage = React.lazy(() => import("@/pages/static/PartnerTermsPage"));
const PrivacyPage = React.lazy(() => import("@/pages/static/PrivacyPage"));
const RefundsPage = React.lazy(() => import("@/pages/static/RefundsPage"));
const ReleasesPage = React.lazy(() => import("@/pages/static/ReleasesPage"));
const ReportGuidePage = React.lazy(() => import("@/pages/static/ReportGuidePage"));
const ReportMarkdownPage = React.lazy(() => import("@/pages/static/ReportMarkdownPage"));
const ScoringPage = React.lazy(() => import("@/pages/static/ScoringPage"));
const KiePage = React.lazy(() => import("@/pages/static/KiePage"));
const ApiDocsPage = React.lazy(() => import("@/pages/static/ApiDocsPage"));
const ShippingPage = React.lazy(() => import("@/pages/static/ShippingPage"));
const SupportersTermsPage = React.lazy(() => import("@/pages/static/SupportersTermsPage"));
const TermsPage = React.lazy(() => import("@/pages/static/TermsPage"));
const SystemHealthPage = React.lazy(() => import("@/pages/static/SystemHealthPage"));

// Dashboard entry: allow access via access key OR agency partner session (no end-user key)
function DashboardEntry({ onBack, onBackToPartner }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'key' | 'agency' | 'required'
  const [accessKey, setAccessKey] = useState(null);
  const [accessKeyInfo, setAccessKeyInfo] = useState(null);

  useEffect(() => {
    const preferAgency = new URLSearchParams(window.location.search).get('agency') === '1';
    const key = localStorage.getItem('cv_access_key');
    let info = null;
    try {
      const s = localStorage.getItem('cv_access_key_info');
      if (s) info = JSON.parse(s);
    } catch (_) {}

    const applyKeyMode = () => {
      if (!key) return false;
      setAccessKey(key);
      setAccessKeyInfo(info);
      setStatus('key');
      return true;
    };

    // Prefer active agency partner session whenever available.
    // This avoids accidentally falling back to an old end-user key and showing partial data.
    partnerGet('/api/partner/me')
      .then((me) => {
        if (me?.partner?.partner_type === 'agency') {
          setStatus('agency');
        } else if (preferAgency) {
          if (!applyKeyMode()) setStatus('required');
        } else if (!applyKeyMode()) {
          setStatus('required');
        }
      })
      .catch(() => {
        if (!applyKeyMode()) setStatus('required');
      });
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen cv-customer-page-wash flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-cv-sky border-t-cv-teal rounded-full" />
      </div>
    );
  }

  if (status === 'required') {
    return (
      <div className="min-h-screen cv-customer-page-wash flex items-center justify-center">
        <div className="cv-card-emboss rounded-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🔑</div>
          <h2 className="text-xl font-bold text-cv-navy mb-2">Access Key Required</h2>
          <p className="text-cv-navy/70 mb-4">Please attach a premium access key on the main page first.</p>
          <a
            href="/"
            className="cv-customer-cta cv-emboss inline-block px-4 py-2 rounded-lg font-semibold"
          >
            Go to Main Page
          </a>
        </div>
      </div>
    );
  }

  return (
    <Dashboard
      accessKey={accessKey}
      accessKeyInfo={accessKeyInfo}
      agencyDashboardMode={status === 'agency'}
      onBack={status === 'agency' ? onBackToPartner : onBack}
    />
  );
}

// Google Analytics: send page_view on every SPA route change. Listens to
// the centralised `cv:locationchange` emitter installed at the top of this
// file; we no longer re-patch `history.pushState` here. Initial load is
// sent by index.html's inline gtag config.
function useGoogleAnalyticsPageView() {
  useEffect(() => {
    const send = () => {
      if (typeof window === 'undefined' || !window.gtag) return;
      window.gtag('config', 'G-K2765LFY0V', { page_path: window.location.pathname + window.location.search });
    };
    window.addEventListener('cv:locationchange', send);
    return () => window.removeEventListener('cv:locationchange', send);
  }, []);
}

// Customer-surface flag. Sets `document.documentElement.dataset.cvSurface`
// to `"admin"` on /admin* and `"customer"` everywhere else, so the brand
// shadcn overrides in `index.css` and the `cv-*` utility classes only apply
// to the customer-facing surface. Admin keeps the neutral shadcn defaults.
// This is invariant 1.1 in `docs/themes/PLAYBOOK.md`.
function useCustomerSurfaceFlag() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const apply = () => {
      const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
      const isAdmin = path === '/admin' || path.startsWith('/admin/');
      document.documentElement.dataset.cvSurface = isAdmin ? 'admin' : 'customer';
    };
    apply();
    window.addEventListener('cv:locationchange', apply);
    return () => window.removeEventListener('cv:locationchange', apply);
  }, []);
}

// Simple router based on URL path, params, and host
function AppRouter() {
  useGoogleAnalyticsPageView();
  useCustomerSurfaceFlag();
  const params = new URLSearchParams(window.location.search);
  const page = params.get('page');
  const pathname = window.location.pathname;
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const hostname = window.location.hostname.toLowerCase();

  // Effective page routing (host-aware):
  // - Explicit ?page=... always wins
  const effectivePage = page || null;

  // Static content pages (must be checked before /partner catch-all)
  const STATIC_ROUTES = {
    '/about': AboutPage,
    '/acceptable-use': AcceptableUsePage,
    '/bot': BotPage,
    '/contact': ContactPage,
    '/cookies': CookiesPage,
    '/dashboard-guide': DashboardGuidePage,
    '/disclaimer': DisclaimerPage,
    '/faq': FaqPage,
    '/knowledge': KnowledgePage,
    '/partner-agreement': PartnerAgreementPage,
    '/partner-disclaimer': PartnerDisclaimerPage,
    '/partner-faq': PartnerFaqPage,
    '/partner-knowledge': PartnerKnowledgePage,
    '/partner-program': PartnerProgramPage,
    '/partner-terms': PartnerTermsPage,
    '/privacy': PrivacyPage,
    '/refunds': RefundsPage,
    '/releases': ReleasesPage,
    '/report-guide': ReportGuidePage,
    '/report-markdown': ReportMarkdownPage,
    '/scoring': ScoringPage,
    '/keyword-intelligence': KiePage,
    '/api-docs': ApiDocsPage,
    '/shipping': ShippingPage,
    '/supporters-terms': SupportersTermsPage,
    '/terms': TermsPage,
    '/health': SystemHealthPage,
  };
  const StaticComponent = STATIC_ROUTES[normalizedPathname];
  if (StaticComponent) return <StaticComponent />;

  // Backward-compat: the Admin Operational Manual used to live at the public
  // URL `/admin-manual`. It now opens inside the admin shell at `/admin/manual`
  // (see `frontend/src/pages/admin/routes.js`). Preserve old bookmarks /
  // sidebar links by redirecting transparently.
  if (normalizedPathname === '/admin-manual') {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash || '';
      window.location.replace(`/admin/manual${hash}`);
    }
    return null;
  }

  // Partner portal
  if (normalizedPathname === '/partner' || normalizedPathname.startsWith('/partner/')) {
    document.title = 'Partner Portal - Crawler Vision';
    return <Partner />;
  }

  // Admin area (placeholder until full Admin Web App)
  if (normalizedPathname === '/admin' || normalizedPathname.startsWith('/admin/')) {
    document.title = 'Admin - Crawler Vision';
    return <Admin />;
  }
  
  // Handle premium payment redirects
  if (normalizedPathname === '/premium/success') {
    document.title = 'Payment Successful - Crawler Vision';
    return <PremiumSuccess />;
  }
  
  if (normalizedPathname === '/premium/cancel') {
    document.title = 'Payment Cancelled - Crawler Vision';
    return <PremiumCancel />;
  }
  
  // Handle Razorpay callback (after redirect from backend)
  if (normalizedPathname === '/premium/razorpay-success') {
    document.title = 'Verifying Payment - Crawler Vision';
    return <RazorpayCallback />;
  }
  
  // Handle Reserve Pool top-up success (Stripe)
  if (normalizedPathname === '/premium/topup-success') {
    document.title = 'Top-up Successful - Crawler Vision';
    return <TopupSuccess />;
  }
  
  // Handle Learning Center page (including SEO slug: /learning/ai-bot-traffic-is-the-new-infrastructure-load)
  const isLearning = normalizedPathname === '/learning' || (normalizedPathname.startsWith('/learning/') && normalizedPathname !== '/learning-page' && normalizedPathname !== '/learning/page') || effectivePage === 'learning';
  if (isLearning) {
    document.title = 'Learning Center - SEO & AI Tips | Crawler Vision';
    return <LearningCenter />;
  }

  // Direct Learning page view (jump to specific page with ?page=N)
  if (normalizedPathname === '/learning-page' || normalizedPathname === '/learning/page') {
    document.title = 'Learning Center Page | Crawler Vision';
    return <LearningPageView />;
  }
  
  if (normalizedPathname === '/dashboard' || effectivePage === 'dashboard') {
    document.title = 'CV Dashboard - Crawler Vision';
    return (
      <DashboardEntry
        onBack={() => window.location.href = '/'}
        onBackToPartner={() => window.location.href = '/partner'}
      />
    );
  }
  
  if (normalizedPathname === '/domain-dashboard' || effectivePage === 'domain-dashboard') {
    document.title = 'Domain Analysis — Progress & History - Crawler Vision';
    
    const accessKey = localStorage.getItem('cv_access_key');
    const accessKeyInfoStr = localStorage.getItem('cv_access_key_info');
    let accessKeyInfo = null;
    try { if (accessKeyInfoStr) accessKeyInfo = JSON.parse(accessKeyInfoStr); } catch (_) {}
    
    if (!accessKey) {
      return (
        <div className="min-h-screen cv-customer-page-wash flex items-center justify-center">
          <div className="cv-card-emboss rounded-xl p-8 max-w-md text-center">
            <div className="text-6xl mb-4">🔑</div>
            <h2 className="text-xl font-bold text-cv-navy mb-2">Access Key Required</h2>
            <p className="text-cv-navy/70 mb-4">Please attach a premium access key on the analyzer page first.</p>
            <a href="/analyzer" className="cv-customer-cta cv-emboss inline-block px-4 py-2 rounded-lg font-semibold">Go to Analyzer</a>
          </div>
        </div>
      );
    }
    
    return (
      <DomainDashboard
        accessKey={accessKey}
        accessKeyInfo={accessKeyInfo}
        onBack={() => window.location.href = '/analyzer'}
      />
    );
  }

  // Analyzer app (accessed via /analyzer, ?page=analyzer, or analyzer subdomain)
  if (normalizedPathname === '/analyzer' || effectivePage === 'analyzer') {
    document.title = 'Analyzer - Crawler Vision';
    return <App />;
  }

  // Default: render homepage
  return <HomePage />;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Suspense fallback={null}>
      <SmallScreenNotice />
      <AppRouter />
      <DashboardCopilotPanel />
      <PublicCopilotPanel />
    </Suspense>
  </React.StrictMode>,
);

